import { type FormEvent, type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import {
  type DialogSummary,
  type LeadContext,
  useAddDialogNote,
  useDeleteDialogNote,
  useDialogNotes,
  useLeadContext,
} from "@/lib/inbox";
import { useAccounts } from "@/lib/accounts";
import { formatDate, formatShortTime } from "@/lib/format";
import { AccountDetailDrawer } from "@/components/accounts/AccountDetailDrawer";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";
import { peerDisplayName } from "./peer";

/** Go to a campaign's card, the way the hand-rolled router in App.tsx moves. */
function goToCampaign(id: string) {
  window.history.pushState(null, "", `/inviter?campaign=${id}`);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

const LINK =
  "rounded-sm text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";
// In the one-line header the link cuts its own text. Left to the line's
// `truncate`, a button — an indivisible box — would vanish whole behind the "…".
const ONE_LINE = "min-w-0 truncate";

/** The campaign's name, leading to the campaign. */
function CampaignLink({
  campaign,
  onNavigate,
  className,
}: {
  campaign: NonNullable<LeadContext["campaign"]>;
  /** Runs before leaving — the drawer closes itself. */
  onNavigate?: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={cn(LINK, className)}
      title={campaign.name}
      onClick={() => {
        onNavigate?.();
        goToCampaign(campaign.id);
      }}
    >
      {campaign.name}
    </button>
  );
}

/** The account a dialog goes through, opening the account's card. */
export function AccountLink({
  dialog,
  onOpen,
  className,
}: {
  dialog: DialogSummary;
  onOpen: (accountId: string) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <button type="button" className={cn(LINK, className)} onClick={() => onOpen(dialog.account_id)}>
      {dialog.account_label ?? t("inbox.account_unnamed")}
    </button>
  );
}

/** Who the conversation is with and through which account — a header line. */
export function LeadVia({
  dialog,
  onOpenAccount,
}: {
  dialog: DialogSummary;
  onOpenAccount: (accountId: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <p className="flex min-w-0 items-baseline gap-1 text-xs text-fg-muted">
      <span className="truncate">
        {dialog.peer_username ? `@${dialog.peer_username} · ` : ""}
        {t("inbox.via")}
      </span>
      <AccountLink dialog={dialog} onOpen={onOpenAccount} className={ONE_LINE} />
    </p>
  );
}

/**
 * The account's card (Обзор / Настройки / Безопасность / Логи) over the inbox.
 * Mounted only once an account is asked for: the card wants the full account
 * row, and that comes with the whole fleet's list.
 */
export function InboxAccountCard({
  accountId,
  onClose,
}: {
  accountId: string;
  onClose: () => void;
}) {
  const accounts = useAccounts();
  const account = accounts.data?.find((a) => a.id === accountId) ?? null;
  return <AccountDetailDrawer account={account} open onOpenChange={(open) => !open && onClose()} />;
}

/**
 * Where the lead came from — a header line: "From: <campaign> · DM", or that
 * the person wrote on their own. Holds its height while the answer loads, so
 * the header does not jump.
 */
export function LeadOrigin({
  dialog,
  onNavigate,
}: {
  dialog: DialogSummary;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const ctx = useLeadContext(dialog.id).data;
  if (!ctx?.campaign) {
    return (
      <p className="truncate text-xs text-fg-muted">
        {ctx ? t(`inbox.origin.${ctx.started_by}`) : "\u00a0"}
      </p>
    );
  }
  return (
    <p className="flex min-w-0 items-baseline gap-1 text-xs text-fg-muted">
      <span className="shrink-0">{t("inbox.origin.from")}</span>
      <CampaignLink campaign={ctx.campaign} onNavigate={onNavigate} className={ONE_LINE} />
      <span className="shrink-0">
        {"· "}
        {t(`inviter.redesign.strategy_short.${ctx.campaign.strategy}`, {
          defaultValue: ctx.campaign.strategy,
        })}
      </span>
    </p>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-fg-muted">{label}</dt>
      <dd className="min-w-0 break-words text-right text-fg-primary">{children}</dd>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2 border-t border-border px-4 py-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-fg-muted">{title}</h3>
      {children}
    </section>
  );
}

/**
 * The operators' notes on the conversation — "has a 50k channel", "write after
 * the weekend". The team's own: every operator of the company reads them, and
 * none goes to Telegram. Mounted per dialog (keyed), so a draft does not follow
 * the operator into another conversation.
 */
function LeadNotes({ dialogId }: { dialogId: string }) {
  const { t } = useTranslation();
  const notes = useDialogNotes(dialogId);
  const add = useAddDialogNote();
  const remove = useDeleteDialogNote();
  const [draft, setDraft] = useState("");

  const save = () => {
    const text = draft.trim();
    if (!text) return;
    add.mutate({ dialogId, text }, { onSuccess: () => setDraft("") });
  };

  return (
    <Section title={t("inbox.notes.title")}>
      {notes.data && notes.data.length > 0 ? (
        <ul className="space-y-2" aria-label={t("inbox.notes.title")}>
          {notes.data.map((note) => (
            <li key={note.id} className="rounded-lg border border-border bg-bg-elevated/40 px-3 py-2">
              <p className="whitespace-pre-wrap break-words text-fg-primary">{note.text}</p>
              <div className="mt-1 flex items-center justify-between gap-2 text-xs text-fg-muted">
                <span className="min-w-0 truncate">
                  {note.author ?? t("inbox.notes.author_gone")} · {formatShortTime(note.created_at)}
                </span>
                <button
                  type="button"
                  onClick={() => remove.mutate({ dialogId, noteId: note.id })}
                  aria-label={t("inbox.notes.delete")}
                  title={t("inbox.notes.delete")}
                  className="shrink-0 rounded-md p-0.5 text-fg-muted hover:bg-danger/10 hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  <X size={14} strokeWidth={2} aria-hidden="true" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        !notes.isLoading && <p className="text-fg-muted">{t("inbox.notes.empty")}</p>
      )}
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          save();
        }}
        className="space-y-1.5"
      >
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={2}
          maxLength={2000}
          placeholder={t("inbox.notes.placeholder") ?? ""}
          aria-label={t("inbox.notes.new")}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              save();
            }
          }}
          className="w-full rounded-lg border border-border bg-bg-primary px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        />
        <div className="flex justify-end">
          <Button
            type="submit"
            size="sm"
            variant="secondary"
            loading={add.isPending}
            disabled={!draft.trim()}
          >
            {t("inbox.notes.add")}
          </Button>
        </div>
      </form>
    </Section>
  );
}

export interface LeadInfoPanelProps {
  dialog: DialogSummary;
  onOpenAccount: (accountId: string) => void;
  onNavigate?: () => void;
}

/**
 * "About the lead": who the person is, the team's notes on them and where they
 * came from — the campaign and its first message, the audience source, the
 * account. The full inbox shows it as a column, the quick one on request.
 */
export function LeadInfoPanel({ dialog, onOpenAccount, onNavigate }: LeadInfoPanelProps) {
  const { t } = useTranslation();
  const context = useLeadContext(dialog.id);
  const ctx = context.data;
  const name = peerDisplayName(dialog);

  return (
    <div className="text-sm">
      <div className="flex items-center gap-3 px-4 py-3">
        <Avatar
          name={name}
          src={dialog.has_avatar ? `/api/inbox/dialogs/${dialog.id}/avatar` : null}
          colorKey={String(dialog.peer_tg_id)}
          size={44}
        />
        <div className="min-w-0">
          <p className="truncate font-medium text-fg-primary">{name}</p>
          <p className="truncate text-xs text-fg-muted">
            {dialog.peer_username ? `@${dialog.peer_username} · ` : ""}
            ID {dialog.peer_tg_id}
          </p>
          {ctx && (ctx.is_premium || ctx.language_code) && (
            <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-fg-muted">
              {ctx.is_premium && <Badge tone="accent">Premium</Badge>}
              {ctx.language_code && (
                <span>{t("inbox.about.language", { code: ctx.language_code })}</span>
              )}
            </p>
          )}
        </div>
      </div>

      <LeadNotes key={dialog.id} dialogId={dialog.id} />

      {context.isLoading ? (
        <p className="border-t border-border px-4 py-3 text-fg-muted">{t("inbox.loading")}</p>
      ) : !ctx ? (
        <p className="border-t border-border px-4 py-3 text-fg-muted">{t("inbox.about.failed")}</p>
      ) : (
        <>
          <Section title={t("inbox.about.origin")}>
            <dl className="space-y-1.5">
              <Row label={t("inbox.about.campaign")}>
                {ctx.campaign ? (
                  <CampaignLink
                    campaign={ctx.campaign}
                    onNavigate={onNavigate}
                    className="text-right"
                  />
                ) : (
                  t(`inbox.origin.${ctx.started_by}`)
                )}
              </Row>
              {ctx.campaign && (
                <Row label={t("inbox.about.method")}>
                  {t(`inviter.redesign.strategy_short.${ctx.campaign.strategy}`, {
                    defaultValue: ctx.campaign.strategy,
                  })}
                </Row>
              )}
              <Row label={t("inbox.about.source")}>
                {ctx.source ?? (ctx.in_audience ? "—" : t("inbox.about.not_in_audience"))}
              </Row>
              <Row label={t("inbox.about.first_contact")}>
                {ctx.first_contact_at ? formatDate(ctx.first_contact_at) : "—"}
              </Row>
              <Row label={t("inbox.about.account")}>
                <AccountLink dialog={dialog} onOpen={onOpenAccount} />
              </Row>
            </dl>
          </Section>

          {ctx.campaign && (
            <Section title={t("inbox.about.first_message")}>
              {ctx.first_message || ctx.first_message_has_media ? (
                <p className="whitespace-pre-wrap break-words rounded-lg border border-accent/30 bg-accent/10 px-3 py-2 text-fg-primary">
                  {ctx.first_message ?? t("inbox.attached_media")}
                </p>
              ) : (
                <p className="text-fg-muted">{t("inbox.about.first_message_none")}</p>
              )}
            </Section>
          )}

          {ctx.channels_total > 0 && (
            <Section title={t("inbox.about.channels", { count: ctx.channels_total })}>
              <ul className="space-y-1">
                {ctx.channels.map((channel) => (
                  <li key={channel} className="truncate text-fg-primary">
                    {channel}
                  </li>
                ))}
                {ctx.channels_total > ctx.channels.length && (
                  <li className="text-xs text-fg-muted">
                    {t("inbox.about.channels_more", {
                      count: ctx.channels_total - ctx.channels.length,
                    })}
                  </li>
                )}
              </ul>
            </Section>
          )}
        </>
      )}
    </div>
  );
}
